import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import {
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import WarningBanner from '@/components/WarningBanner'
import { HoverHelp } from '@/tableControls'
import { useDefaultIncompleteReportMonth } from '@/hooks/useDefaultIncompleteReportMonth'
import {
  deleteGiftCardUpload,
  downloadGiftCardUpload,
  fetchGiftCardReportFromClover,
  getGiftCardMonth,
  getGiftCardMonthsSummary,
  markGiftCardMonthComplete,
  reconcileGiftCardMonth,
  uploadGiftCardReport,
  type ExpectedReconcilingItems,
  type MonthDetailResponse,
  type ReconcileResponse,
  type StoreBreakdownItem,
} from '@/services/giftCards'
import { isCloverAuthError } from '@/services/cloverExtension'
import { toast } from '@/store/toastStore'
import { useCloverReportsStore } from '@/store/cloverReportsStore'
import { useCloverNavStore } from '@/store/cloverNavStore'
import { formatApiError } from '@/utils/apiError'
import { reportMonthLabel } from '../distribution-waterfall/waterfallJeStatus'
import {
  formatGiftCardMoney,
  formatQboFetchedAt,
  formatReportAsOf,
  isVarianceExplained,
  pickSingleGiftCardUpload,
  unexplainedVarianceCellClassName,
} from './giftCardsFormat'
import { mergeGiftCardMonthSelectOptions } from './giftCardsMonthStatus'
import { Button } from '@cottage/design-system/internal-tooling/ui'

type Props = {
  canEdit: boolean
}

function isUnassignedStore(store: StoreBreakdownItem): boolean {
  return store.store.toLowerCase() === 'unassigned'
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function appendUniqueWarnings(target: string[], seen: Set<string>, messages: string[]) {
  for (const message of messages) {
    if (seen.has(message)) continue
    seen.add(message)
    target.push(message)
  }
}

export default function GiftCardsReconcile({ canEdit }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dragCounterRef = useRef(0)
  const selectedMonthRef = useRef<string | null>(null)
  const autoFetchAttemptedRef = useRef<Set<string>>(new Set())
  const setShowAuthRecovery = useCloverReportsStore((state) => state.setShowAuthRecovery)
  const fetchCloverNavStatus = useCloverNavStore((s) => s.fetchNavStatus)
  const [dragging, setDragging] = useState(false)
  const [monthsSummary, setMonthsSummary] = useState<
    Awaited<ReturnType<typeof getGiftCardMonthsSummary>>['months']
  >([])
  const [monthsSummaryReady, setMonthsSummaryReady] = useState(false)
  const [monthDetail, setMonthDetail] = useState<MonthDetailResponse | null>(null)
  const [reconcileResult, setReconcileResult] = useState<ReconcileResponse | null>(null)
  const [monthLoading, setMonthLoading] = useState(false)
  const [monthError, setMonthError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [fetchNotPublishedMessage, setFetchNotPublishedMessage] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [reconciling, setReconciling] = useState(false)
  const [markingComplete, setMarkingComplete] = useState(false)

  const monthSelectOptions = useMemo(
    () => mergeGiftCardMonthSelectOptions(monthsSummary),
    [monthsSummary],
  )

  const [selectedMonth, setSelectedMonth] = useDefaultIncompleteReportMonth(
    monthSelectOptions,
    monthsSummaryReady,
  )

  const monthLabel = selectedMonth ? reportMonthLabel(selectedMonth) : ''
  selectedMonthRef.current = selectedMonth

  const stores = useMemo(() => {
    if (reconcileResult?.stores?.length) return reconcileResult.stores
    return monthDetail?.stores ?? []
  }, [monthDetail?.stores, reconcileResult?.stores])

  const reconSnapshot = monthDetail?.recon ?? null
  const upload = monthDetail?.upload ?? null

  const totals = useMemo(() => {
    if (reconcileResult) {
      return {
        clover: reconcileResult.clover_outstanding_total,
        qbo: reconcileResult.qbo_balance_total,
        variance: reconcileResult.variance_total,
      }
    }
    if (reconSnapshot) {
      return {
        clover: upload?.clover_outstanding_total ?? '0',
        qbo: reconSnapshot.qbo_balance_total,
        variance: reconSnapshot.variance_total,
      }
    }
    return null
  }, [reconcileResult, reconSnapshot, upload?.clover_outstanding_total])

  const expectedItems: ExpectedReconcilingItems | null = useMemo(() => {
    if (reconcileResult?.expected_reconciling_items) {
      return reconcileResult.expected_reconciling_items
    }
    return monthDetail?.expected_reconciling_items ?? null
  }, [monthDetail?.expected_reconciling_items, reconcileResult?.expected_reconciling_items])

  const warnings = useMemo(() => {
    const items: string[] = []
    const seen = new Set<string>()
    const appendOne = (message: string | null | undefined) => {
      if (!message || seen.has(message)) return
      seen.add(message)
      items.push(message)
    }

    appendOne(upload?.as_of_warning)
    if (upload?.parsed && isStringArray(upload.parsed.warnings)) {
      appendUniqueWarnings(items, seen, upload.parsed.warnings)
    }
    if (reconcileResult?.warnings?.length) {
      appendUniqueWarnings(items, seen, reconcileResult.warnings)
    }
    const hasUnassignedVariance = stores.some(
      (store) => isUnassignedStore(store) && !isVarianceExplained(store),
    )
    if (hasUnassignedVariance) {
      appendOne(
        'QBO gift card liability has an unassigned department balance; review the Unassigned row.',
      )
    }
    return items
  }, [
    upload?.as_of_warning,
    upload?.parsed,
    reconcileResult?.warnings,
    stores,
  ])

  const loadMonthsSummary = useCallback(async () => {
    try {
      const data = await getGiftCardMonthsSummary()
      setMonthsSummary(data.months)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load months'))
      setMonthsSummary([])
    } finally {
      setMonthsSummaryReady(true)
    }
    void fetchCloverNavStatus({ refresh: true })
  }, [fetchCloverNavStatus])

  const loadMonthDetail = useCallback(async (reportMonth: string, options?: { quiet?: boolean }) => {
    if (!options?.quiet) {
      setMonthLoading(true)
      setMonthError(null)
    }
    try {
      const detail = await getGiftCardMonth(reportMonth)
      if (selectedMonthRef.current !== reportMonth) return null
      setMonthDetail(detail)
      setMonthError(null)
      return detail
    } catch (err) {
      if (selectedMonthRef.current !== reportMonth) return null
      const message = formatApiError(err, 'Failed to load month detail')
      setMonthError(message)
      setMonthDetail(null)
      toast.error(message)
      return null
    } finally {
      if (!options?.quiet && selectedMonthRef.current === reportMonth) setMonthLoading(false)
    }
  }, [])

  const refreshAll = useCallback(async () => {
    setReconcileResult(null)
    await loadMonthsSummary()
    if (selectedMonth) {
      await loadMonthDetail(selectedMonth)
    }
  }, [loadMonthDetail, loadMonthsSummary, selectedMonth])

  const runReconcile = useCallback(
    async (reportMonth: string, opts?: { force?: boolean; silent?: boolean }) => {
      if (!canEdit) return
      setReconciling(true)
      try {
        const result = await reconcileGiftCardMonth(reportMonth, { force: opts?.force ?? false })
        if (selectedMonthRef.current !== reportMonth) return
        setReconcileResult(result)
        if (!opts?.silent) {
          toast.success(opts?.force ? 'QBO balances refreshed' : 'Reconcile complete')
        }
        await loadMonthsSummary()
        await loadMonthDetail(reportMonth, { quiet: true })
      } catch (err) {
        if (selectedMonthRef.current !== reportMonth) return
        toast.error(formatApiError(err, 'Failed to reconcile'))
      } finally {
        if (selectedMonthRef.current === reportMonth) {
          setReconciling(false)
        }
      }
    },
    [canEdit, loadMonthDetail, loadMonthsSummary],
  )

  const tablePopulated = stores.length > 0 && totals !== null
  const isMonthComplete = monthDetail?.ui_status === 'complete'
  const showMarkCompleteControl = tablePopulated && (canEdit || isMonthComplete)

  const handleMarkComplete = useCallback(async () => {
    if (!canEdit || !selectedMonth || !tablePopulated || isMonthComplete) return
    const unexplained =
      totals && expectedItems
        ? !isVarianceExplained({
            variance: totals.variance,
            expected_reconciling_items: expectedItems.total,
          })
        : monthDetail?.hint === 'Unexplained variance'
    if (unexplained) {
      const ok = window.confirm(
        `Mark this month complete even though variance is not fully explained? This checks “Reconciled outstanding Clover gift card balances” on Income allocation for ${monthLabel}.`,
      )
      if (!ok) return
    }
    setMarkingComplete(true)
    try {
      const detail = await markGiftCardMonthComplete(selectedMonth)
      if (selectedMonthRef.current !== selectedMonth) return
      setMonthDetail(detail)
      setReconcileResult(null)
      toast.success('Gift card reconcile marked complete')
      await loadMonthsSummary()
      await loadMonthDetail(selectedMonth, { quiet: true })
    } catch (err) {
      if (selectedMonthRef.current !== selectedMonth) return
      toast.error(formatApiError(err, 'Failed to mark complete'))
    } finally {
      if (selectedMonthRef.current === selectedMonth) {
        setMarkingComplete(false)
      }
    }
  }, [
    canEdit,
    selectedMonth,
    tablePopulated,
    isMonthComplete,
    totals,
    expectedItems,
    monthDetail?.hint,
    monthLabel,
    loadMonthsSummary,
    loadMonthDetail,
  ])

  const handleFetchFromClover = useCallback(
    async (
      reportMonth: string,
      opts?: { silent?: boolean; hadUpload?: boolean },
    ) => {
      if (!canEdit) return false
      setFetching(true)
      setUploadError(null)
      setFetchNotPublishedMessage(null)
      try {
        await fetchGiftCardReportFromClover(reportMonth)
        if (selectedMonthRef.current !== reportMonth) return false
        setReconcileResult(null)
        await loadMonthDetail(reportMonth, { quiet: true })
        await loadMonthsSummary()
        if (!opts?.silent) {
          toast.success(
            opts?.hadUpload ? 'Refreshed from Clover' : 'Fetched from Clover',
          )
        }
        setFetching(false)
        await runReconcile(reportMonth, { force: true, silent: true })
        return true
      } catch (err) {
        if (selectedMonthRef.current !== reportMonth) return false
        if (isCloverAuthError(err)) {
          setShowAuthRecovery(true)
        }
        const message = formatApiError(err, 'Failed to fetch from Clover')
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 404) {
          setFetchNotPublishedMessage(message)
        } else if (!opts?.silent) {
          toast.error(message)
          setUploadError(message)
        }
        return false
      } finally {
        if (selectedMonthRef.current === reportMonth) {
          setFetching(false)
        }
      }
    },
    [canEdit, loadMonthDetail, loadMonthsSummary, runReconcile, setShowAuthRecovery],
  )

  useEffect(() => {
    void loadMonthsSummary()
  }, [loadMonthsSummary])

  useEffect(() => {
    if (!selectedMonth) {
      setMonthDetail(null)
      setReconcileResult(null)
      return
    }
    let cancelled = false
    setReconcileResult(null)
    setMonthDetail(null)
    setDragging(false)
    setFetchNotPublishedMessage(null)
    dragCounterRef.current = 0

    async function loadAndMaybeReconcile(reportMonth: string) {
      const detail = await loadMonthDetail(reportMonth)
      if (cancelled) return
      if (canEdit && !detail?.upload) {
        if (!autoFetchAttemptedRef.current.has(reportMonth)) {
          autoFetchAttemptedRef.current.add(reportMonth)
          const fetched = await handleFetchFromClover(reportMonth, { silent: true })
          if (cancelled) return
          if (fetched) return
        }
        return
      }
      if (canEdit && detail?.upload) {
        await runReconcile(reportMonth, { force: false, silent: true })
      }
    }

    void loadAndMaybeReconcile(selectedMonth)
    return () => {
      cancelled = true
    }
  }, [canEdit, handleFetchFromClover, loadMonthDetail, runReconcile, selectedMonth])

  const handleUpload = async (file: File) => {
    if (!canEdit || !selectedMonth) return
    const reportMonth = selectedMonth
    setUploading(true)
    setUploadError(null)
    try {
      await uploadGiftCardReport(reportMonth, file)
      setReconcileResult(null)
      await loadMonthDetail(reportMonth, { quiet: true })
      await loadMonthsSummary()
      toast.success('Gift card report uploaded')
      setUploading(false)
      await runReconcile(reportMonth, { force: true, silent: true })
    } catch (err) {
      const message = formatApiError(err, 'Failed to upload report')
      setUploadError(message)
      toast.error(message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const applyPickedFiles = (files: ArrayLike<File>) => {
    if (!canEdit || uploading) return
    const picked = pickSingleGiftCardUpload(files)
    if ('error' in picked) {
      setUploadError(picked.error)
      toast.error(picked.error)
      return
    }
    void handleUpload(picked.file)
  }

  const dropDisabled = !canEdit || uploading || fetching

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (dropDisabled) return
    dragCounterRef.current += 1
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setDragging(true)
    }
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (dropDisabled) return
    dragCounterRef.current -= 1
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setDragging(false)
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = dropDisabled ? 'none' : 'copy'
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current = 0
    setDragging(false)
    if (dropDisabled) return
    applyPickedFiles(event.dataTransfer.files)
  }

  const handleDeleteUpload = async () => {
    if (!canEdit || !selectedMonth || !upload) return

    const needsConfirm = Boolean(reconSnapshot)
    if (needsConfirm) {
      const confirmed = window.confirm('Delete this upload? Reconcile data for this month will be cleared.')
      if (!confirmed) return
    }

    setUploading(true)
    try {
      await deleteGiftCardUpload(selectedMonth, needsConfirm)
      toast.success('Upload deleted')
      setReconcileResult(null)
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete upload'))
    } finally {
      setUploading(false)
    }
  }

  const handleReconcile = async () => {
    if (!canEdit || !selectedMonth || !upload) return
    await runReconcile(selectedMonth, { force: true })
  }

  const handleDownload = async () => {
    if (!selectedMonth) return
    try {
      await downloadGiftCardUpload(selectedMonth)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to download upload'))
    }
  }

  const asOfLabel = formatReportAsOf(upload?.report_as_of ?? null)
  const asOfMismatch = Boolean(upload?.as_of_warning)

  const qboFetchedLabel = formatQboFetchedAt(
    reconcileResult?.qbo_fetched_at ?? reconSnapshot?.qbo_fetched_at ?? null,
  )
  const reportAsOfCaption = formatReportAsOf(upload?.report_as_of ?? null)

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            <HoverHelp label="Month" aria-label="Gift cards month help">
              Months run from January 2025 through the last completed calendar month.
            </HoverHelp>
          </h2>
        </div>
        <div className="px-5 py-4">
          <label htmlFor="gift-cards-month" className="sr-only">Month</label>
          <ReportMonthSelect
            id="gift-cards-month"
            options={monthSelectOptions}
            value={selectedMonth || null}
            onChange={(id) => id && setSelectedMonth(id)}
            disabled={!monthSelectOptions.length}
            placeholder="Select month"
            className="w-[300px] max-w-full text-sm"
          />
          {monthError && !monthLoading ? (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {monthError}
            </p>
          ) : null}
        </div>
      </section>

      {selectedMonth ? (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-900">Upload</h2>
            {canEdit ? (
              <button
                type="button"
                className="btn btn-primary text-xs"
                disabled={fetching || uploading}
                onClick={() =>
                  void handleFetchFromClover(selectedMonth, { hadUpload: Boolean(upload) })
                }
              >
                {fetching
                  ? 'Fetching…'
                  : upload
                    ? 'Refresh from Clover'
                    : 'Fetch from Clover'}
              </button>
            ) : null}
          </div>
          <div className="px-5 py-4">
            <div
              className={`rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
                dragging
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              } ${uploading || fetching ? 'opacity-60' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {monthLoading && !upload && !fetching ? (
                <p className="text-center text-sm text-gray-500">Loading month…</p>
              ) : fetching && !upload ? (
                <p className="text-center text-sm text-gray-500">Fetching from Clover…</p>
              ) : !upload ? (
                <div className="text-center">
                  <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
                  <p className="mt-2 text-sm text-gray-700">
                    {uploading ? (
                      'Uploading…'
                    ) : (
                      <>
                        Or upload a Daily Promo Summary .xls if Clover has not published the file
                        yet, or{' '}
                        <label
                          className={`font-medium text-brand-700 hover:text-brand-800 ${
                            dropDisabled ? 'pointer-events-none' : 'cursor-pointer'
                          }`}
                        >
                          browse
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xls,application/vnd.ms-excel"
                            className="sr-only"
                            disabled={dropDisabled}
                            onChange={(e) => {
                              applyPickedFiles(e.target.files ?? [])
                              if (fileInputRef.current) fileInputRef.current.value = ''
                            }}
                          />
                        </label>
                      </>
                    )}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Daily Promo Summary for {monthLabel}, as of the month&apos;s last day. One .xls
                    file.
                  </p>
                </div>
              ) : (
                <div className="flex min-w-0 flex-col items-start">
                  <div className="flex max-w-full items-center gap-1">
                    <DocumentIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                    <p
                      className="min-w-0 truncate text-sm font-medium text-gray-900"
                      title={upload.source_filename}
                    >
                      {upload.source_filename}
                    </p>
                    {canEdit ? (
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Delete upload"
                        disabled={uploading}
                        onClick={() => void handleDeleteUpload()}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Download upload"
                      onClick={() => void handleDownload()}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {asOfLabel ? (
                    <p
                      className={`mt-1 pl-6 text-xs ${
                        asOfMismatch ? 'text-amber-800' : 'text-gray-500'
                      }`}
                    >
                      {asOfMismatch ? (
                        <HoverHelp label={`As of ${asOfLabel}`} aria-label="Report as-of warning">
                          {upload.as_of_warning ??
                            'Report as-of is not the last day of the selected month.'}
                        </HoverHelp>
                      ) : (
                        <>As of {asOfLabel}</>
                      )}
                    </p>
                  ) : null}
                  {canEdit && !uploading ? (
                    <p className="mt-3 text-xs text-gray-500">
                      Drop an .xls here to replace this file, or{' '}
                      <label
                        className={`font-medium text-brand-700 hover:text-brand-800 ${
                          dropDisabled ? 'pointer-events-none' : 'cursor-pointer'
                        }`}
                      >
                        browse
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xls,application/vnd.ms-excel"
                          className="sr-only"
                          disabled={dropDisabled}
                          onChange={(e) => {
                            applyPickedFiles(e.target.files ?? [])
                            if (fileInputRef.current) fileInputRef.current.value = ''
                          }}
                        />
                      </label>
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            {uploadError ? (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {uploadError}
              </p>
            ) : null}
            {fetchNotPublishedMessage ? (
              <p className="mt-3 text-sm text-amber-800" role="status">
                {fetchNotPublishedMessage}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {selectedMonth ? (
        <>
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">Reconcile</h2>
              {canEdit ? (
                <button
                  type="button"
                  className="btn btn-primary text-xs"
                  disabled={!upload || reconciling}
                  onClick={() => void handleReconcile()}
                >
                  {totals
                    ? reconciling
                      ? 'Syncing…'
                      : 'Sync QBO'
                    : reconciling
                      ? 'Reconciling…'
                      : 'Reconcile'}
                </button>
              ) : null}
            </div>

            <div className="space-y-4 p-5">
              {warnings.length > 0 ? (
                <div className="space-y-2">
                  {warnings.map((warning, index) => (
                    <WarningBanner key={index} message={warning} />
                  ))}
                </div>
              ) : null}

              {!upload && !monthLoading && !fetching ? (
                <p className="text-sm text-gray-600">
                  Fetch or upload a Daily Promo Summary .xls for this month, then reconcile against
                  QBO.
                </p>
              ) : null}
              {upload && reconciling && !totals ? (
                <p className="text-sm text-gray-500">
                  Fetching QBO Gift Card Liability balances…
                </p>
              ) : null}
              {upload && !totals && !reconciling ? (
                <p className="text-sm text-gray-600">
                  Compare this month&apos;s Clover outstanding against QBO Gift Card Liability as of
                  month-end.
                </p>
              ) : null}

              {stores.length > 0 && totals ? (
                <div className="overflow-x-auto">
                  <table className="table-forecast min-w-full border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <th className="text-left">Store</th>
                        <th className="text-right">
                          <HoverHelp
                            label="Clover outstanding"
                            aria-label="Clover outstanding help"
                          >
                            Life to date outstanding.
                          </HoverHelp>
                        </th>
                        <th className="text-right">QBO balance</th>
                        <th className="text-right">Variance</th>
                        <th className="text-right">
                          <HoverHelp
                            label="Expected items"
                            aria-label="Expected reconciling items help"
                          >
                            Sold but not activated from daily Clover JEs, minus later fulfilment
                            activations.
                          </HoverHelp>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stores.map((store) => (
                        <tr key={`${store.store}-${store.location_id ?? 'na'}`}>
                          <td>
                            {isUnassignedStore(store) ? (
                              <HoverHelp label={store.store} aria-label="Unassigned help">
                                QBO liability balance not tagged to a mapped store department.
                              </HoverHelp>
                            ) : (
                              store.store
                            )}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatGiftCardMoney(store.clover_outstanding)}
                          </td>
                          <td className="text-right tabular-nums">
                            {formatGiftCardMoney(store.qbo_balance)}
                          </td>
                          <td className="text-right tabular-nums">
                            <span className={unexplainedVarianceCellClassName(store)}>
                              {formatGiftCardMoney(store.variance)}
                            </span>
                          </td>
                          <td className="text-right tabular-nums">
                            {formatGiftCardMoney(store.expected_reconciling_items)}
                          </td>
                        </tr>
                      ))}
                      <tr className="font-semibold">
                        <td>Total</td>
                        <td className="text-right tabular-nums">
                          {formatGiftCardMoney(totals.clover)}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatGiftCardMoney(totals.qbo)}
                        </td>
                        <td className="text-right tabular-nums">
                          <span
                            className={
                              expectedItems
                                ? unexplainedVarianceCellClassName({
                                    variance: totals.variance,
                                    expected_reconciling_items: expectedItems.total,
                                  })
                                : 'text-amber-800'
                            }
                          >
                            {formatGiftCardMoney(totals.variance)}
                          </span>
                        </td>
                        <td className="text-right tabular-nums">
                          {formatGiftCardMoney(expectedItems?.total)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : null}
              {stores.length > 0 && totals ? (
                <div className="text-xs text-gray-500">
                  {qboFetchedLabel ? `QBO fetched ${qboFetchedLabel}` : null}
                  {qboFetchedLabel && reportAsOfCaption ? ' · ' : null}
                  {reportAsOfCaption ? `Report as of ${reportAsOfCaption}` : null}
                </div>
              ) : null}
            </div>
          </section>
          {showMarkCompleteControl ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                disabled={isMonthComplete || markingComplete || reconciling || !canEdit}
                onClick={() => void handleMarkComplete()}
                data-testid="gift-cards-mark-complete"
              >
                {isMonthComplete
                  ? 'Complete'
                  : markingComplete
                    ? 'Marking…'
                    : 'Mark Complete'}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
